import React from "react";
import { useSearchParams } from "react-router-dom";

/**
 * React hook to sync query param with a piece of state.
 * INITIAL STATE WILL BE OVERRIDDEN BY QUERY PARAM
 * @param param name for the param
 * @param initialValue initial value for the piece of state.
 * @returns { [T, (newValue: T) => void] }
 */
function useQueryParam<T>(
  param: string,
  initialValue: T,
): [T, (newValue: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = React.useState<T>(initialValue);

  const deserializedValue = React.useMemo(() => {
    const paramValue = searchParams.get(param);
    if (paramValue) return JSON.parse(paramValue) as T;
    return undefined;
  }, [searchParams, param]);

  // On first call:
  //  - Sync param => state on load.
  React.useEffect(() => {
    if (deserializedValue) setValue(deserializedValue);
  }, []);

  // Sync param => state
  React.useEffect(() => {
    if (!deserializedValue) setValue(initialValue);
    else if (deserializedValue && deserializedValue !== value)
      setValue(deserializedValue);
  }, [deserializedValue]);

  // Sync state => param
  const setValueWrapper = React.useCallback(
    (newValue: T) => {
      setValue(newValue);
      setSearchParams({ [param]: JSON.stringify(newValue) });
    },
    [setValue, setSearchParams, param],
  );

  return [value, setValueWrapper];
}

export { useQueryParam };
